"use client"

/**
 * LeoThinkingBackdrop — ambient thinking chrome from Leo AI Q2:
 * gradient blobs (rest vs thinking tempo) + drifting dot cloud overlay.
 */

import * as React from "react"

import {
  AnimatedBlobBackground,
  type BlobIntensity,
} from "@/components/animated-blob-background"
import { AiThinkingOverlay } from "@/components/ui/ai-thinking-surface"
import { cn } from "@/lib/utils"

export type LeoThinkingBackdropProps = {
  /** Show blob layer (disable on very small surfaces if needed). */
  blobsEnabled?: boolean
  /** Faster blob drift while the model is working. */
  thinking?: boolean
  /** Drifting dot pattern overlay. */
  dotsActive?: boolean
  /** `high` on empty hero; `normal` in conversation. */
  intensity?: BlobIntensity
  /**
   * `landing` — blobs anchor under the hero composer when idle.
   * `panel` — blobs fill the aside (Ask Leo sidebar).
   */
  variant?: "landing" | "panel"
  className?: string
}

/**
 * Fades the rail's blob layer out toward the top.
 *
 * Clipping it instead (`top-1/3` + the wrapper's `overflow-hidden`) cut straight
 * through three 480px circles, so what should have been the softest part of the
 * glow became a hard horizontal line with the fully saturated blob interior
 * sitting right underneath it — the wash read upside down, densest at its top
 * edge. Masking keeps the geometry intact: the blobs stay anchored to the bottom
 * where the density belongs, and the top simply stops existing.
 */
const PANEL_BLOB_MASK = [
  "linear-gradient(to bottom,",
  "transparent 0%,",
  "rgba(0,0,0,0.10) 42%,",
  "rgba(0,0,0,0.52) 68%,",
  "#000 88%)",
].join(" ")

const PANEL_BLOB_MASK_STYLE: React.CSSProperties = {
  maskImage: PANEL_BLOB_MASK,
  WebkitMaskImage: PANEL_BLOB_MASK,
}

export function LeoThinkingBackdrop({
  blobsEnabled = true,
  thinking = false,
  dotsActive = false,
  intensity = "normal",
  variant = "landing",
  className,
}: LeoThinkingBackdropProps) {
  const hasConversation = intensity === "normal"
  const showDots = dotsActive
  const isPanel = variant === "panel"

  return (
    <div
      className={cn(
        "pointer-events-none absolute inset-0 z-0 overflow-hidden",
        className,
      )}
      aria-hidden
    >
      {blobsEnabled ? (
        <div
          style={isPanel ? PANEL_BLOB_MASK_STYLE : undefined}
          className={cn(
            "leo-ai-blob-layer absolute inset-x-0 z-0 motion-reduce:transition-none",
            // The blobs are ~480px circles anchored to the bottom of this layer.
            // On the landing canvas they sit side by side; in a ~400px rail they
            // stack, and three overlapping tints blur into one flat wash — so the
            // panel halves the opacity and fades the top out (below).
            isPanel &&
              "leo-ai-blob-layer--panel inset-y-0 opacity-85 transition-opacity duration-700 ease-out",
            !isPanel && !hasConversation
              ? "leo-ai-blob-layer--idle-anchor bottom-0 top-auto opacity-100"
              : !isPanel && "inset-0",
            !isPanel && thinking && "opacity-80 transition-opacity duration-700 ease-out",
            !isPanel &&
              !thinking &&
              hasConversation &&
              "opacity-60 transition-opacity duration-700 ease-out",
            !isPanel && !thinking && !hasConversation && "opacity-100",
          )}
        >
          <AnimatedBlobBackground
            enabled
            intensity={intensity}
            thinking={thinking}
          />
        </div>
      ) : null}
      {/* Tighter grid and a smaller drift cloud in the rail: the landing's 260px
          cloud is wider than the panel, so every dot would be lit at once and the
          pattern would read as static noise instead of something moving. */}
      <AiThinkingOverlay
        active={showDots}
        cloudRadius={isPanel ? 150 : 260}
        gridSize={isPanel ? 12 : 14}
        fillClassName={
          isPanel ? "fill-brand/40 dark:fill-brand/50" : "fill-brand/25 dark:fill-brand/35"
        }
        className="z-[1]"
      />
    </div>
  )
}
